"use client";

import type { Order, OrderItem } from "@/types/prisma";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/lib/utils";

type OrderWithItems = Order & { items: OrderItem[] };

const STATUS_OPTIONS = [
  "ALL",
  "PENDING",
  "PAID",
  "PROCESSING",
  "PACKED",
  "SHIPPED",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "CANCELLED",
  "RETURNED"
] as const;

function statusTone(
  status: Order["status"]
): "success" | "deal" | "prime" | "default" {
  if (status === "DELIVERED") return "success";
  if (status === "CANCELLED" || status === "RETURNED") return "deal";
  if (status === "SHIPPED" || status === "OUT_FOR_DELIVERY") return "prime";
  return "default";
}

export function OrdersClient({ orders }: { orders: OrderWithItems[] }) {
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_OPTIONS)[number]>("ALL");

  const filtered = useMemo(
    () =>
      statusFilter === "ALL"
        ? orders
        : orders.filter((o) => o.status === statusFilter),
    [orders, statusFilter]
  );

  return (
    <div className="space-y-5">
      {/* Filter bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar sm:flex-wrap sm:pb-0">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`shrink-0 rounded-full border px-3 py-1 text-xs font-bold transition ${
              statusFilter === s
                ? "border-amazon-orange bg-amazon-orange/10 text-amazon-orange"
                : "border-slate-200 text-slate-600 hover:border-amazon-orange dark:border-white/10 dark:text-slate-300"
            }`}
          >
            {s === "ALL" ? `All (${orders.length})` : s}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-slate-500">No orders match this filter.</p>
      )}

      {filtered.map((order) => (
        <article
          key={order.id}
          className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900"
        >
          <div className="grid grid-cols-2 gap-3 bg-slate-100 px-4 py-3 sm:px-5 sm:py-4 text-sm dark:bg-white/10 sm:grid-cols-4">
            <div>
              <p className="text-xs font-black uppercase text-slate-500">Order placed</p>
              <p className="font-bold">
                {new Date(order.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-xs font-black uppercase text-slate-500">Total</p>
              <p className="font-bold">{formatPrice(order.total)}</p>
            </div>
            <div>
              <p className="text-xs font-black uppercase text-slate-500">Status</p>
              <Badge tone={statusTone(order.status)}>{order.status}</Badge>
            </div>
            <div>
              <p className="text-xs font-black uppercase text-slate-500">Order ID</p>
              <p className="truncate font-bold text-xs">{order.id}</p>
            </div>
          </div>

          <div className="divide-y divide-slate-200 dark:divide-white/10">
            {order.items.map((item) => (
              <div key={item.id} className="flex gap-4 p-5">
                <Link
                  href={`/products/${item.productId}`}
                  className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md bg-slate-100"
                >
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    sizes="80px"
                    className="object-cover"
                  />
                </Link>
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-slate-950 dark:text-white line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    Qty {item.quantity} &bull; {formatPrice(item.price)}
                  </p>
                  <Link
                    href={`/products/${item.productId}`}
                    className="mt-3 inline-flex text-sm font-bold text-amazon-teal hover:text-amazon-orange"
                    onClick={async () => {
                      // We can't add to cart without the full product data
                      // Navigate to product page instead
                    }}
                  >
                    Buy it again
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Shipping address */}
          <div className="border-t border-slate-200 px-5 py-3 text-xs text-slate-500 dark:border-white/10">
            Shipped to: {order.shippingFullName}, {order.shippingLine1}, {order.shippingCity},{" "}
            {order.shippingState} {order.shippingPincode}
          </div>
        </article>
      ))}
    </div>
  );
}
